// The console shell: sidebar navigation, the org picker, the theme toggle, the
// read-only banner (shown while the console is dark, pre writes-cutover), and
// the routed outlet.

import { NavLink, Outlet } from "react-router-dom"
import { useWritesEnabled } from "../auth/AdminAuthContext.js"
import { getOidcConfig, logout } from "../auth/oidc.js"
import { fr } from "../i18n/fr.js"
import { useOrg } from "../state/org.js"
import { useTheme } from "../theme/theme.js"
import { SolyaLogo } from "../ui/SolyaLogo.js"
import { LogoutIcon, MoonIcon, SunIcon } from "../ui/icons.js"
import { Alert, IconButton, Select } from "../ui/primitives.js"

function navClass({ isActive }: { isActive: boolean }): string {
  return isActive ? "sidebar__link sidebar__link--active" : "sidebar__link"
}

export function Layout() {
  const writesEnabled = useWritesEnabled()
  const { orgs, activeOrg, setActiveOrg } = useOrg()
  const { theme, toggle: toggleTheme } = useTheme()
  // Only a real Keycloak session can be signed out of (the stub/dev mode has no
  // issuer to end-session against); hide the control when there is no OIDC config.
  const oidc = getOidcConfig()

  return (
    <div className="layout">
      <nav className="sidebar">
        <SolyaLogo title={fr.app.title} subtitle={fr.app.subtitle} />

        <div>
          <div className="field__label" style={{ marginBottom: 8 }}>
            {fr.app.orgLabel}
          </div>
          <Select
            aria-label={fr.app.orgLabel}
            value={activeOrg ?? ""}
            onChange={(e) => setActiveOrg(e.target.value)}
          >
            {orgs.length === 0 ? <option value="">{fr.app.orgPlaceholder}</option> : null}
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="sidebar__nav">
          <NavLink to="/roles" className={navClass}>
            {fr.app.nav.roles}
          </NavLink>
          <NavLink to="/permissions" className={navClass}>
            {fr.app.nav.permissions}
          </NavLink>
          <NavLink to="/teams" className={navClass}>
            {fr.app.nav.teams}
          </NavLink>
          <NavLink to="/users" className={navClass}>
            {fr.app.nav.users}
          </NavLink>
        </div>

        {/* Light is the default; this is the opt-in back into dark. */}
        <div className="sidebar__footer">
          <IconButton
            label={theme === "dark" ? fr.app.theme.toLight : fr.app.theme.toDark}
            onClick={toggleTheme}
          >
            {theme === "dark" ? <SunIcon /> : <MoonIcon />}
          </IconButton>
          {oidc ? (
            <IconButton label={fr.app.logout} onClick={() => logout(oidc)}>
              <LogoutIcon />
            </IconButton>
          ) : null}
        </div>
      </nav>

      <main className="main">
        {writesEnabled ? null : (
          <div style={{ marginBottom: 16 }}>
            <Alert variant="warning">{fr.app.writesDisabledBanner}</Alert>
          </div>
        )}
        <Outlet />
      </main>
    </div>
  )
}
